import { createApi, FetchArgs, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_SLICE_TAGS } from "../constants/constants.ts";
import {
  ConfigurationDisplayDto,
  ConfigurationDto,
  EntraSecurityAdminDto,
  LoginRequest,
  LoginResponse,
  SimpleSecurityDefinitionAdminDto,
  SimpleSecurityUserAdminDto
} from "./ApiTypes.ts";
import appConfig from "../config/appConfig.ts";
import AuthService from "./AuthService.ts";

const dynamicBaseQuery = async (
  args: string | FetchArgs,
  api: any,
  extraOptions: any
) => {
  const baseUrl = appConfig.SERVER_API_URL;

  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      let authService = AuthService.getInstance()
      const token = await authService.getToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      const company = localStorage.getItem("company");
      if (company) {
        headers.set("X-Company", company);
      }
      return headers;
    },
  });

  return rawBaseQuery(args, api, extraOptions);
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    API_SLICE_TAGS.configurations,
    API_SLICE_TAGS.securityDefinitions,
    API_SLICE_TAGS.users,
  ],
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "login",
        method: "POST",
        body: credentials,
      }),
    }),

    getConfigurations: builder.query<ConfigurationDto[], void>({
      query: () => ({
        url: "Admin/Configurations",
        method: "GET",
      }),
      providesTags: (result) => 
        result
          ? result.map(({ id }) => ({ type: API_SLICE_TAGS.configurations, id }))
          : [{ type: API_SLICE_TAGS.configurations }],
    }),

    getConfiguration: builder.query<ConfigurationDto, string>({
      query: (configurationId) => ({
        url: `AdminConfiguration/${configurationId}`,
        method: "GET",
      }),
      providesTags: (result, error, configurationId) => [
        { type: API_SLICE_TAGS.configurations, id: configurationId },
      ],
    }),
    getConfigurationDisplayList: builder.query<ConfigurationDisplayDto[], void>({
      query: () => ({
        url: "AdminConfiguration/ConfigurationList",
        method: "GET",
      }),
      providesTags: (result) => 
        result
          ? result.map(({ id }) => ({ type: API_SLICE_TAGS.configurationDisplays, id }))
          : [{ type: API_SLICE_TAGS.configurationDisplays }],
    }),
    updateConfiguration: builder.mutation<ConfigurationDto, { configuration: ConfigurationDto}>({
      query: ({ configuration }) => ({
        url: `AdminConfiguration/Configuration`,
        method: "POST",
        body: configuration,
      }),
      invalidatesTags: (result, error, { configuration }) => [
        { type: API_SLICE_TAGS.configurations, id: configuration.id },
        { type: API_SLICE_TAGS.configurationDisplays }
      ],
    }),

    getSimpleSecurityDefinition: builder.query<SimpleSecurityDefinitionAdminDto, string>({
      query: (configurationId) => ({
        url: `Admin/${configurationId}/SimpleSecurityDefinition`,
        method: "GET",
      }),
      providesTags: (result, error, configurationId) => [
        { type: API_SLICE_TAGS.securityDefinitions, id: configurationId },
      ],
    }),

    addSimpleSecurityDefinition: builder.mutation<SimpleSecurityDefinitionAdminDto, { configurationId: string, simpleSecurityDefinition: SimpleSecurityDefinitionAdminDto }>({
      query: ({ configurationId, simpleSecurityDefinition }) => ({
        url: `Admin/${configurationId}/SimpleSecurityDefinition`,
        method: "POST",
        body: simpleSecurityDefinition,
      }),
      invalidatesTags: (result, error, { configurationId }) => [
        { type: API_SLICE_TAGS.securityDefinitions, id: configurationId },
      ],
    }),

    updateSimpleSecurityDefinition: builder.mutation<SimpleSecurityDefinitionAdminDto, { configurationId: string, simpleSecurityDefinition: SimpleSecurityDefinitionAdminDto }>({
      query: ({ simpleSecurityDefinition }) => ({
        url: `Admin/SimpleSecurityDefinition/${simpleSecurityDefinition.id}`,
        method: "PUT",
        body: simpleSecurityDefinition,
      }),
      invalidatesTags: (result, error, { configurationId, simpleSecurityDefinition }) => [
        { type: API_SLICE_TAGS.securityDefinitions, id: configurationId },
        { type: API_SLICE_TAGS.securityDefinitions, id: simpleSecurityDefinition.id },
      ],
    }),

    getSimpleSecurityUsers: builder.query<SimpleSecurityUserAdminDto[], string>({
      query: (configurationId) => ({
        url: `Admin/${configurationId}/Users`,
        method: "GET",
      }),
      providesTags: (result, error, configurationId) => [
        { type: API_SLICE_TAGS.users, id: configurationId },
      ],
    }),

    addSimpleSecurityUser: builder.mutation<SimpleSecurityUserAdminDto, { configurationId: string, simpleSecurityUser: SimpleSecurityUserAdminDto }>({
      query: ({ configurationId, simpleSecurityUser }) => ({
        url: `Admin/${configurationId}/User`,
        method: "POST",
        body: simpleSecurityUser,
      }),
      invalidatesTags: (result, error, { configurationId }) => [
        { type: API_SLICE_TAGS.users, id: configurationId },
      ],
    }),

    updateSimpleSecurityUser: builder.mutation<SimpleSecurityUserAdminDto, { configurationId: string, simpleSecurityUser: SimpleSecurityUserAdminDto }>({
      query: ({ configurationId, simpleSecurityUser }) => ({
        url: `Admin/UpdateUser/${simpleSecurityUser.id}`,
        method: "PUT",
        body: simpleSecurityUser,
      }),
      invalidatesTags: (result, error, { configurationId, simpleSecurityUser }) => [
        { type: API_SLICE_TAGS.users, id: configurationId },
      ],
    }),
    deleteSimpleSecurityUser: builder.mutation<void, { configurationId: string, simpleSecurityUserId: string }>({
      query: ({ configurationId, simpleSecurityUserId }) => ({
        url: `Admin/DeleteUser/${simpleSecurityUserId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { configurationId, simpleSecurityUserId }) => [
        { type: API_SLICE_TAGS.users, id: simpleSecurityUserId },
        { type: API_SLICE_TAGS.users, id: configurationId },
      ],
    }),

    getEntraSecurity: builder.query<EntraSecurityAdminDto[], null>({
      query: () => ({
        url: `Admin/EntraSecurity`,
        method: "GET",
      }),
      providesTags: (result, error) => [
        { type: API_SLICE_TAGS.entraSecurity},
      ],
    }),
    addEntraSecurity: builder.mutation<EntraSecurityAdminDto, { entraSecurity: EntraSecurityAdminDto }>({
      query: ({ entraSecurity }) => ({
        url: `Admin/EntraSecurity`,
        method: "POST",
        body: entraSecurity,
      }),
      invalidatesTags: (result, error) => [
        { type: API_SLICE_TAGS.entraSecurity },
      ],
    }),

    updateEntraSecurity: builder.mutation<EntraSecurityAdminDto, { entraSecurityId: string, entraSecurity: EntraSecurityAdminDto }>({
      query: ({ entraSecurityId, entraSecurity }) => ({
        url: `Admin/UpdateEntraSecurity/${entraSecurityId}`,
        method: "PUT",
        body: entraSecurity,
      }),
      invalidatesTags: (result, error, { entraSecurityId }) => [
        { type: API_SLICE_TAGS.entraSecurity, id: entraSecurityId },
      ],
    }),

    deleteEntraSecurity: builder.mutation<void, { entraSecurityId: string }>({
      query: ({ entraSecurityId }) => ({
        url: `Admin/DeleteEntraSecurity/${entraSecurityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { entraSecurityId }) => [
        { type: API_SLICE_TAGS.entraSecurity},
      ],
    }),
    getAzureConfig: builder.query<{ clientId: string; tenantId: string }, void>({
      query: () => "AdminLogin/AzureConfig",
    }),

    deleteConfiguration: builder.mutation<void, string>({
      query : (id) => ({
        url: `AdminConfiguration/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [API_SLICE_TAGS.configurations, API_SLICE_TAGS.configurationDisplays],
    }),

    duplicateConfiguration: builder.mutation<ConfigurationDto, { id: string; newName: string }>({
    query: ({id, newName}) => ({
      url: `AdminConfiguration/Duplicate/${id}`,
      method: 'POST',
      body: JSON.stringify(newName),
      headers: {
      'Content-Type': 'application/json',
      },
    }),
    invalidatesTags: [API_SLICE_TAGS.configurations, API_SLICE_TAGS.configurationDisplays],
    }),

  }),
});

export const {
  useLoginMutation,
  useGetConfigurationsQuery,
  useGetConfigurationQuery,
  useGetConfigurationDisplayListQuery,
  useUpdateConfigurationMutation,
  useGetSimpleSecurityDefinitionQuery,
  useAddSimpleSecurityDefinitionMutation,
  useUpdateSimpleSecurityDefinitionMutation,
  useGetSimpleSecurityUsersQuery,
  useAddSimpleSecurityUserMutation,
  useUpdateSimpleSecurityUserMutation,
  useDeleteSimpleSecurityUserMutation,
  useGetEntraSecurityQuery,
  useAddEntraSecurityMutation,
  useUpdateEntraSecurityMutation,
  useDeleteEntraSecurityMutation,
  useGetAzureConfigQuery,
  useDeleteConfigurationMutation,
  useDuplicateConfigurationMutation
} = apiSlice;
